import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../../../lib/api';
import { usePermesso } from '../../../../context/PermessiContext';
import { formatCurrency } from '../../../../utils/formatters';
import { CommessaDettaglio } from '../SchedaCommessa';
import { API_DOCS } from './TabSal.api';
import ApiInspector from '../../../../components/ApiInspector';

// ── Tipi ─────────────────────────────────────────────────────────────────────

type StatoSal = 'BOZZA' | 'EMESSO' | 'APPROVATO_CLIENTE' | 'APPROVATO_BACKOFFICE' | 'FATTURATO';

interface SalListItem {
  id: number;
  societaId: number;
  sedeId: number | null;
  commessaId: number;
  ordineId: number | null;
  numero: string | null;
  stato: StatoSal;
  dataEmissione: string | null;
  dataApprovazione: string | null;
  tipoApprovazione: string | null;
  importoTotale: number | string | null;
  createdAt: string;
  ordine: { id: number; numero: string; flussoCommerciale: string } | null;
  commessa: { id: number; numero: string; descrizione: string | null; clienteRagioneSociale: string | null } | null;
  _count: { righe: number };
}

interface TabSalProps {
  commessa: CommessaDettaglio;
  onRefresh: () => void;
  isBlocked: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—';

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

const STATO_CFG: Record<StatoSal, { label: string; cls: string }> = {
  BOZZA:                { label: 'Bozza',           cls: 'bop-badge-neutral' },
  EMESSO:               { label: 'Emesso',          cls: 'bop-badge-info'    },
  APPROVATO_CLIENTE:    { label: 'App. cliente',    cls: 'bop-badge-success' },
  APPROVATO_BACKOFFICE: { label: 'App. backoffice', cls: 'bop-badge-success' },
  FATTURATO:            { label: 'Fatturato',       cls: 'bop-badge-success' },
};

const STATI_APPROVATI: StatoSal[] = ['APPROVATO_CLIENTE', 'APPROVATO_BACKOFFICE', 'FATTURATO'];

// ── Componente ────────────────────────────────────────────────────────────────

/**
 * TabSal — vista in SOLA LETTURA dei SAL della commessa.
 *
 * I SAL si creano dalla sezione SAL scegliendo la commessa (o dalla scheda
 * ordine). Da qui si consultano e si apre la scheda SAL in una nuova tab
 * (pattern di TabDistinte). Un SAL può coprire più ordini: in quel caso
 * ordine è null e la colonna mostra "Più ordini".
 */
export default function TabSal({ commessa }: TabSalProps) {
  const { can } = usePermesso();
  const canView = can('commerciale.sal', 'view');

  const [sal,     setSal]     = useState<SalListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const load = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true); setError('');
    try {
      // @api GET /api/sal?societaId={societaId}&commessaId={commessaId} -> {"success":true,"data":[{"id":"number","societaId":"number","sedeId":"number|null","commessaId":"number","ordineId":"number","numero":"string|null","stato":"BOZZA|EMESSO|APPROVATO_CLIENTE|APPROVATO_BACKOFFICE|FATTURATO","dataEmissione":"string|null","dataApprovazione":"string|null","tipoApprovazione":"string|null","importoTotale":"number","createdAt":"string","ordine":{"id":"number","numero":"string","flussoCommerciale":"string"},"commessa":{"id":"number","numero":"string","descrizione":"string|null","clienteRagioneSociale":"string|null"},"_count":{"righe":"number"}}]}
      const res = await apiFetch<{ success: boolean; data?: SalListItem[]; error?: unknown }>(
        `/api/sal?societaId=${commessa.societaId}&commessaId=${commessa.id}`
      );
      if (!res.success) {
        setError(estraiErrore({ payload: res }, 'Errore nel caricamento dei SAL'));
        setSal([]);
      } else {
        setSal(res.data ?? []);
      }
    } catch (e: unknown) {
      setError(estraiErrore(e, 'Errore nel caricamento dei SAL'));
      setSal([]);
    } finally {
      setLoading(false);
    }
  }, [canView, commessa.societaId, commessa.id]);

  useEffect(() => { load(); }, [load]);

  if (!canView) {
    return <div className="bop-empty-state">Permesso SAL necessario</div>;
  }

  if (loading) {
    return <div className="bop-empty-state">Caricamento SAL…</div>;
  }

  if (error) {
    return <div className="bop-text-danger">{error}</div>;
  }

  // ── KPI ─────────────────────────────────────────────────────────────────────
  const importoTotale = sal.reduce((sum, s) => sum + (Number(s.importoTotale) || 0), 0);
  const nApprovati = sal.filter(s => STATI_APPROVATI.includes(s.stato)).length;

  const apriSal = (id: number) => {
    window.open('/commerciale/attivo/sal/' + id, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bop-tab-sal">
      {/* KPI */}
      <div className="bop-sal__header">
        <div className="bop-sal__kpi-row">
          <div>
            <span className="bop-sal__kpi-label">SAL:</span>
            <span className="bop-sal__kpi-value--total">{sal.length}</span>
          </div>
          <div>
            <span className="bop-sal__kpi-label">Importo totale:</span>
            <span className="bop-sal__kpi-value--total">{formatCurrency(importoTotale)}</span>
          </div>
          <div>
            <span className="bop-sal__kpi-label">Approvati:</span>
            <span className="bop-sal__kpi-value--approved">{nApprovati}</span>
          </div>
        </div>
      </div>

      {sal.length === 0 ? (
        <div className="bop-empty-state">
          Nessun SAL. I SAL si creano dalla scheda di un ordine appalto confermato.
        </div>
      ) : (
        <div className="bop-card">
          <table className="bop-table bop-table--clickable">
            <thead>
              <tr>
                <th>Numero</th>
                <th>Ordine</th>
                <th>Stato</th>
                <th className="bop-text-right">Importo</th>
                <th>Data emissione</th>
                <th>Approvazione</th>
              </tr>
            </thead>
            <tbody>
              {sal.map(s => {
                const statoCfg = STATO_CFG[s.stato] ?? { label: s.stato, cls: 'bop-badge-neutral' };
                const isApprovato = STATI_APPROVATI.includes(s.stato);
                return (
                  <tr key={s.id} onClick={() => apriSal(s.id)}>
                    <td className="bop-sal__cell-numero">
                      {s.numero ?? <span className="bop-badge bop-badge-neutral">Bozza</span>}
                    </td>
                    <td>
                      {s.ordine
                        ? s.ordine.numero
                        : <span className="bop-badge bop-badge-neutral">Più ordini</span>}
                    </td>
                    <td><span className={`bop-badge ${statoCfg.cls}`}>{statoCfg.label}</span></td>
                    <td className="bop-text-right bop-sal__cell-importo">
                      {formatCurrency(s.importoTotale != null ? Number(s.importoTotale) : null)}
                    </td>
                    <td className="bop-sal__cell-date">{fmtDate(s.dataEmissione)}</td>
                    <td className="bop-sal__cell-approvazione">
                      {isApprovato ? (
                        <span className="bop-sal__approvazione-info">
                          {s.tipoApprovazione === 'CLIENTE' ? 'Cliente' : 'Backoffice'} — {fmtDate(s.dataApprovazione)}
                        </span>
                      ) : (
                        <span className="bop-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}
